
import {useState, useEffect, SetStateAction} from 'react';
import {
    PartnerGetPaging,
    PartnerSave,
    PartnerDelete
} from 'services/partnerService';
import {PartnerModal} from "components/SavePartnerModal";
import ConfirmDialog from "popup/ConfirmDialog";
import {toast} from "react-hot-toast";
import LoadingOverlay from "components/Static/LoadingOverlay";
import { Edit, Plus, Trash } from 'lucide-react';

export default function Partners() {
    const [partners, setPartners] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPartner, setCurrentPartner] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);

    const fetchPartners = async () => {
        try {
            setLoading(true);
            const response = await PartnerGetPaging({});
            if (response.result) {
                setPartners(response.result.items);
            } else {
                setError(response.message || 'Failed to fetch partners');
            }
        } catch (err) {
            setError('An error occurred while fetching partners');
        } finally {
            setLoading(false);
        }
    };

    const savePartner = async (data: any) => {
        try {
            setLoading(true);
            const response = await PartnerSave(data);

            if (response.result) {
                toast.success('Lưu đơn vị liên kết thành công');
                setIsModalOpen(false);
                await fetchPartners();
                return {success: true};
            } else {
                toast.error(response.message || 'Lỗi khi lưu đơn vị liên kết');
                return {success: false, message: response.message};
            }
        } catch (err: any) {
            toast.error(err.message || 'Đã có lỗi xảy ra khi lưu đơn vị liên kết');
            return {success: false, message: err.message};
        } finally {
            setLoading(false);
        }
    };

    const deletePartner = async (id: string) => {
        try {
            setLoading(true);
            const response = await PartnerDelete(id);


            toast.success('Xóa đơn vị liên kết thành công');
            await fetchPartners();
                return {success: true};
        } catch (error: any) {
            toast.error(error.message || 'Đã có lỗi xảy ra khi xóa đơn vị liên kết');
            return {success: false, message: error.message};
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setCurrentPartner(null);
        setIsModalOpen(true);
    };

    const handleEdit = (partner: SetStateAction<null>) => {
        setCurrentPartner(partner);
        setIsModalOpen(true);
    };

    const openConfirm = (id: string) => {
        setSelectedPartnerId(id);
        setIsConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (selectedPartnerId === null) return;
        await deletePartner(selectedPartnerId);
        setIsConfirmOpen(false);
    };

    useEffect(() => {
        fetchPartners();
    }, []);

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h1 className="text-3xl font-bold text-gray-800">Quản lý đơn vị liên kết</h1>

            <div className="flex justify-end items-center mb-6 mt-6">
                <button
                    onClick={handleAddNew}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    <Plus className="mr-2"/>
                    Thêm đơn vị liên kết
                </button>
            </div>

            {loading ? <LoadingOverlay message="Đang tải dữ liệu..." /> : (
                <div className="mx-auto">
                    <div className="grid grid-cols-1 gap-8">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="w-full overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Tên
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Mã đơn vị
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Thao tác
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {partners.map((partner) => (
                                        <tr key={partner.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-gray-900">
                                                    {partner.name}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-gray-500">
                                                    {partner.description}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                <button
                                                    onClick={() => handleEdit(partner)}
                                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                                >
                                                    <Edit className="inline mr-1"/> Sửa
                                                </button>
                                                <button
                                                    onClick={() => partner.id && openConfirm(partner.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    <Trash className="inline mr-1"/> Xóa
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <PartnerModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={savePartner}
                initialData={currentPartner}
            />

            <ConfirmDialog
                isOpen={isConfirmOpen}
                title="Xác nhận xóa"
                description="Bạn có chắc chắn muốn xóa đơn vị liên kết này không?"
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleDelete}
            />
        </div>
    );
}